// admin/src/App.jsx — the admin shell (D1 skeleton; D2b fleshes out). The role gate here is
// a CURTAIN (UX): /v1/me says role, and non-admins see the refusal card — but every admin
// route re-checks server-side (functions/admin.js requireAdmin). Domain separation is not
// security; users.role is.

import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { Router, Link, useRoute } from '@common/router.js';
import { initAuth } from '@common/auth.js';
import { makeApi } from '@common/api.js';
import Users from './screens/Users.jsx';
import UserDetail from './screens/UserDetail.jsx';
import Books from './screens/Books.jsx';
import BookEditor from './screens/BookEditor.jsx';
import PlansPacks from './screens/PlansPacks.jsx';
import Audit from './screens/Audit.jsx';
import Maintenance from './screens/Maintenance.jsx';

const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN || null;
const FIREBASE_CONFIG = import.meta.env.VITE_FIREBASE_CONFIG ? JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG) : null;
const API_URL = import.meta.env.VITE_API_URL || '';

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

const ROUTES = [
  { path: '/', render: () => <Users /> },
  { path: '/users/:id', render: (p) => <UserDetail id={p.id} /> },
  { path: '/books', render: () => <Books /> },
  { path: '/books/:version', render: (p) => <BookEditor version={p.version} /> },
  { path: '/plans', render: () => <PlansPacks /> },
  { path: '/audit', render: () => <Audit /> },
  { path: '/maintenance', render: () => <Maintenance /> },
];

function Rail() {
  const { pathname } = useRoute();
  const { me, auth } = useAdmin();
  const is = (p) => (p === '/' ? pathname === '/' || pathname.startsWith('/users') : pathname.startsWith(p));
  return (
    <nav className="rail">
      <div className="brand"><em>Q</em> Admin</div>
      <div className="nav">
        <Link className={is('/') ? 'active' : ''} to="/">Users & credits</Link>
        <Link className={is('/books') ? 'active' : ''} to="/books">Rate card</Link>
        <Link className={is('/plans') ? 'active' : ''} to="/plans">Plans & packs</Link>
        <Link className={is('/audit') ? 'active' : ''} to="/audit">Audit trail</Link>
        <Link className={is('/maintenance') ? 'active' : ''} to="/maintenance">Maintenance</Link>
      </div>
      <div className="foot">
        <div className="who">{me ? me.email : ''}<br />admin · role from the server</div>
        <button className="link" onClick={() => auth.signOut()}>Sign out</button>
      </div>
    </nav>
  );
}

// D-AUTH parity §1: the admin signin gains email/password (mirror of the studio signin
// card — signin ONLY, no signup here; accounts are made in the studio app and the role
// wall stays exactly as is). Google keeps the popup — the admin lives on quicketing.com,
// outside the studio OAuth redirect estate. Admin ships English-only (no i18n runtime).
function SignInCard({ auth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const guard = (fn) => async () => { setErr(null); try { await fn(); } catch (e) { setErr(e.message); } };
  const input = { inlineSize: '100%', padding: '10px 12px', marginBlockEnd: 8, boxSizing: 'border-box' };
  return (
    <div className="content" style={{ maxInlineSize: 380, marginInline: 'auto', paddingBlockStart: '14vh' }}>
      <h1>Quocia Studio — Admin</h1>
      <div className="card">
        {auth.mode === 'dev' ? (
          <button className="btn primary" onClick={guard(() => auth.signInDev())}>Dev sign-in (admin token)</button>
        ) : (
          <>
            <button className="btn primary" style={{ inlineSize: '100%' }} onClick={guard(() => auth.signInGoogle())}>Sign in with Google</button>
            <div className="muted" style={{ textAlign: 'center', margin: '10px 0', fontSize: 12 }}>or</div>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={input} />
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={input} />
            <button className="btn" style={{ inlineSize: '100%' }} onClick={guard(() => auth.signInEmail(email, password))}>Sign in with email</button>
          </>
        )}
        {err && <p style={{ color: '#c0392b', fontSize: 13, margin: '10px 0 0' }}>{err}</p>}
      </div>
    </div>
  );
}

export default function App() {
  const auth = useMemo(() => initAuth(DEV_TOKEN ? { devToken: DEV_TOKEN } : { firebaseConfig: FIREBASE_CONFIG }), []);
  const api = useMemo(() => makeApi({ baseUrl: API_URL, getToken: () => auth.getToken() }), [auth]);
  const [authed, setAuthed] = useState(null);
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState(false); // /v1/me itself failed (network/500) — distinct from "not admin"
  const [maint, setMaint] = useState(null);  // MAINT-MODE §7: the maintenance switch, shared by the header banner + the screen

  const loadMe = useCallback(async () => {
    setMe(null); setMeErr(false);
    const r = await api.get('me');
    if (r.kind === 'ok') setMe(r.body); else setMeErr(true);
  }, [api]);

  const refreshMaint = useCallback(async () => {
    const r = await api.get('admin/settings/maintenance');
    if (r.kind === 'ok') setMaint(r.body);
    return r;
  }, [api]);

  useEffect(() => auth.onChange((u) => {
    if (!u) { setAuthed(false); setMe(null); setMeErr(false); setMaint(null); return; }
    setAuthed(true);
    loadMe();
  }), [auth, loadMe]);

  // once we know we're an admin, load the maintenance state so the banner is live everywhere.
  useEffect(() => { if (me && me.role === 'admin') refreshMaint(); }, [me, refreshMaint]);

  if (authed === null) return <div className="content muted">Loading…</div>;
  if (!authed) return <SignInCard auth={auth} />;
  // CURTAIN: hold until /v1/me RESOLVES — never mount the Router (and fire /v1/admin/* fetches)
  // while the role is still unknown. The server wall is airtight either way; this kills the noisy
  // 403 flashes and the never-resolving shell when /v1/me itself fails.
  if (meErr) {
    return (
      <div className="content" style={{ maxInlineSize: 460, marginInline: 'auto', paddingBlockStart: '14vh' }}>
        <div className="card">
          <h2>Couldn’t load your admin profile</h2>
          <p className="muted">The server didn’t answer /v1/me. Check your connection and retry.</p>
          <div className="row">
            <button className="btn primary" onClick={loadMe}>Retry</button>
            <button className="btn" onClick={() => auth.signOut()}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }
  if (!me) return <div className="content muted">Loading…</div>;
  if (me.role !== 'admin') {
    return (
      <div className="content" style={{ maxInlineSize: 460, marginInline: 'auto', paddingBlockStart: '14vh' }}>
        <div className="card">
          <h2>This account can’t use the admin tool</h2>
          <p className="muted">Signed in as {me.email} (role: {me.role}). Admin access is granted server-side — the URL is not the lock.</p>
          <button className="btn" onClick={() => auth.signOut()}>Sign out</button>
        </div>
      </div>
    );
  }
  return (
    <AdminCtx.Provider value={{ api, me, auth, maint, setMaint, refreshMaint }}>
      <Router routes={ROUTES} fallback={<Users />}>
        {(view) => (
          <div className="layout">
            <Rail />
            <main className="content">
              {maint && maint.on && (
                <div className="banner warn" style={{ marginBlockStart: 0 }}>
                  ⚠ <b>Maintenance mode is ON</b> — the studio app is dark to everyone except admins.
                  {maint.message ? ` Message shown: “${maint.message}”` : ''} <Link to="/maintenance">Manage</Link>
                </div>
              )}
              {view}
            </main>
          </div>
        )}
      </Router>
    </AdminCtx.Provider>
  );
}
